import { useEffect, useState } from "react";
import { supabase } from "../../services/supabaseClient";
import { clearAppDataCache } from "../../services/supabaseData";
import {
  resetPasswordForEmail,
  sendPasswordSetupEmail,
  signInWithPassword,
  signUpWithProfile,
  startGoogleSignIn,
  syncUserProfile,
  updateAccount,
  updateRecoveryPassword,
  upsertExplicitProfile,
} from "./authApi";
import {
  PASSWORD_RECOVERY_PENDING_KEY,
  clearAuthCallbackUrl,
  getAuthErrorMessage,
  getAuthProviders,
  getAuthUrlError,
  getUserProfileFields,
  hasPasswordRecoveryUrl,
  isPasswordRecoveryPending,
  setPasswordRecoveryPending,
} from "./authSessionUtils";
import {
  getAppRouteFromPathname,
  getAuthModeFromRoute,
} from "../navigation/appRoutes";

const getInitialAuthMode = () => {
  if (typeof window === "undefined") return "signin";

  return (
    getAuthModeFromRoute(getAppRouteFromPathname(window.location.pathname)) ||
    "signin"
  );
};

export default function useAuthSession() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState(getInitialAuthMode);
  const [authFirstName, setAuthFirstName] = useState("");
  const [authLastName, setAuthLastName] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authPasswordConfirm, setAuthPasswordConfirm] = useState("");
  const [authStatus, setAuthStatus] = useState(null);
  const [authDataStatus, setAuthDataStatus] = useState(null);
  const [signupConfirmationEmail, setSignupConfirmationEmail] = useState("");
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [passwordResetComplete, setPasswordResetComplete] = useState(false);
  const [passwordRecoveryActive, setPasswordRecoveryActive] = useState(false);

  const user = session?.user || null;
  const userId = user?.id || null;
  const accountProfile = getUserProfileFields(user);
  const accountFirstName = accountProfile.firstName;
  const accountLastName = accountProfile.lastName;
  const accountProviders = getAuthProviders(user);
  const accountNeedsPasswordSetup =
    accountProviders.includes("google") && !accountProviders.includes("email");

  const syncProfileStatus = async (nextUser) => {
    const error = await syncUserProfile(nextUser);
    if (error) {
      setAuthDataStatus({
        type: "error",
        message: error.message || "Nao foi possivel atualizar o perfil.",
      });
    }
  };

  useEffect(() => {
    let mounted = true;
    const recoveryFromUrl = hasPasswordRecoveryUrl();
    const authUrlError = getAuthUrlError();

    if (recoveryFromUrl) {
      setPasswordRecoveryPending(true);
      setAuthMode("recovery");
      setPasswordRecoveryActive(true);
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      clearAuthCallbackUrl();
      setSession(data.session);
      if (authUrlError) {
        setAuthStatus({
          type: "error",
          message: getAuthErrorMessage({ message: authUrlError }),
        });
      }
      if (data.session && isPasswordRecoveryPending()) {
        setAuthMode("recovery");
        setPasswordRecoveryActive(true);
        setAuthStatus(null);
        setSignupConfirmationEmail("");
        setPasswordResetEmail("");
        setPasswordResetComplete(false);
      } else if (data.session) {
        setAuthMode("signin");
        setAuthStatus(null);
        setSignupConfirmationEmail("");
        syncProfileStatus(data.session.user);
      }
      setAuthLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "PASSWORD_RECOVERY") {
          setPasswordRecoveryPending(true);
          setAuthMode("recovery");
          setPasswordRecoveryActive(true);
          setAuthStatus(null);
          setSignupConfirmationEmail("");
          setPasswordResetEmail("");
          setPasswordResetComplete(false);
        }

        if (event === "SIGNED_IN" || event === "PASSWORD_RECOVERY") {
          clearAuthCallbackUrl();
        }

        if (nextSession && isPasswordRecoveryPending()) {
          setAuthMode("recovery");
          setPasswordRecoveryActive(true);
          setAuthStatus(null);
          setSignupConfirmationEmail("");
          setPasswordResetEmail("");
          setPasswordResetComplete(false);
        } else if (event === "SIGNED_IN" && nextSession?.user) {
          setPasswordRecoveryPending(false);
          setPasswordRecoveryActive(false);
          setAuthMode("signin");
          setAuthStatus(null);
          syncProfileStatus(nextSession.user);
        }

        if (!nextSession && isPasswordRecoveryPending()) {
          setAuthMode("recovery");
          setPasswordRecoveryActive(true);
          setAuthStatus(null);
          setSignupConfirmationEmail("");
          setPasswordResetEmail("");
          setPasswordResetComplete(false);
        } else if (!nextSession) {
          setAuthMode("signin");
          setAuthStatus(null);
          setSignupConfirmationEmail("");
          setPasswordResetEmail("");
          setPasswordRecoveryPending(false);
          setPasswordRecoveryActive(false);
        }

        setSession((currentSession) => {
          if (
            currentSession?.user?.id === nextSession?.user?.id &&
            currentSession?.access_token === nextSession?.access_token
          ) {
            return currentSession;
          }
          return nextSession;
        });
      }
    );

    const handleStorage = (event) => {
      if (event.key !== PASSWORD_RECOVERY_PENDING_KEY) return;

      if (event.newValue === "1") {
        setAuthMode("recovery");
        setPasswordRecoveryActive(true);
        setAuthStatus(null);
        setSignupConfirmationEmail("");
        setPasswordResetEmail("");
        setPasswordResetComplete(false);
      } else {
        setPasswordRecoveryActive(false);
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      mounted = false;
      window.removeEventListener("storage", handleStorage);
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!authDataStatus) return undefined;
    const timeout = window.setTimeout(() => setAuthDataStatus(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [authDataStatus]);

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setAuthStatus(null);
    setAuthDataStatus(null);
    setSignupConfirmationEmail("");
    setPasswordResetEmail("");
    setPasswordResetComplete(false);
    setAuthLoading(true);

    try {
      const firstName = authFirstName.trim();
      const lastName = authLastName.trim();
      const phone = authPhone.trim();
      const email = authEmail.trim();

      if (authMode === "signup") {
        if (!firstName || !lastName) {
          throw new Error("Informe nome e sobrenome para criar a conta.");
        }

        if (authPassword !== authPasswordConfirm) {
          throw new Error("As senhas nao conferem.");
        }
      }

      if (authMode === "reset") {
        await resetPasswordForEmail(email);
        setPasswordResetEmail(email);
        setAuthLoading(false);
        return;
      }

      if (authMode === "recovery") {
        if (authPassword !== authPasswordConfirm) {
          throw new Error("As senhas nao conferem.");
        }

        const { error } = await updateRecoveryPassword(authPassword);
        if (error) throw error;

        setPasswordRecoveryPending(false);
        await supabase.auth.signOut();
        clearAppDataCache(userId);
        setSession(null);
        setPasswordRecoveryActive(false);
        setPasswordResetComplete(true);
        setAuthMode("signin");
        setAuthPassword("");
        setAuthPasswordConfirm("");
        setAuthLoading(false);
        return;
      }

      const action =
        authMode === "signup"
          ? signUpWithProfile({
              email,
              password: authPassword,
              firstName,
              lastName,
              phone,
            })
          : signInWithPassword({
              email,
              password: authPassword,
            });

      const { data, error } = await action;
      if (error) throw error;

      if (data.session && data.user) {
        clearAppDataCache(data.user.id);
        setSession(data.session);

        const { error: profileError } = await upsertExplicitProfile({
          userId: data.user.id,
          email: data.user.email || email,
          firstName: firstName || data.user.user_metadata?.first_name || "",
          lastName: lastName || data.user.user_metadata?.last_name || "",
          phone,
        });
        if (profileError) {
          setAuthDataStatus({
            type: "error",
            message: profileError.message || "Nao foi possivel atualizar o perfil.",
          });
        }
      }

      if (authMode === "signup" && !data.session) {
        setSignupConfirmationEmail(email);
        setAuthPassword("");
        setAuthPasswordConfirm("");
        setAuthPhone("");
      }
    } catch (error) {
      setAuthStatus({
        type: "error",
        message: getAuthErrorMessage(error),
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    const signedOutUserId = userId;
    await supabase.auth.signOut();
    clearAppDataCache(signedOutUserId);
    setAuthMode("signin");
    setAuthStatus(null);
    setSignupConfirmationEmail("");
    setPasswordResetEmail("");
    setPasswordResetComplete(false);
    setPasswordRecoveryPending(false);
    setPasswordRecoveryActive(false);
    setSession(null);
  };

  const handleGoogleSignIn = async () => {
    setAuthStatus(null);
    setAuthDataStatus(null);
    setSignupConfirmationEmail("");
    setPasswordResetEmail("");
    setPasswordResetComplete(false);
    setPasswordRecoveryPending(false);
    setPasswordRecoveryActive(false);

    const { error } = await startGoogleSignIn();

    if (error) {
      setAuthStatus({
        type: "error",
        message: getAuthErrorMessage(error),
      });
    }
  };

  const handleAuthModeChange = (nextMode) => {
    setAuthMode(nextMode);
    setAuthStatus(null);
    setSignupConfirmationEmail("");
    setPasswordResetEmail("");
    setPasswordResetComplete(false);

    if (nextMode === "signin" || nextMode === "reset") {
      setAuthPhone("");
      setAuthPassword("");
      setAuthPasswordConfirm("");
    }
  };

  const handleAccountUpdate = async ({
    firstName,
    lastName,
    currentPassword,
    password,
    passwordConfirm,
  }) => {
    const { updatedUser, session: nextSession } = await updateAccount({
      user,
      userId,
      firstName,
      lastName,
      currentPassword,
      password,
      passwordConfirm,
    });

    if (nextSession) {
      setSession(nextSession);
    } else if (updatedUser) {
      setSession((currentSession) =>
        currentSession ? { ...currentSession, user: updatedUser } : currentSession
      );
    }

    setAuthDataStatus({
      type: "success",
      message: "Dados da conta atualizados.",
    });
  };

  const handleAccountPasswordSetup = async () => {
    const { error } = await sendPasswordSetupEmail(user);
    if (error) throw error;

    setAuthDataStatus({
      type: "success",
      message: "Enviamos a verificacao para cadastrar sua senha.",
    });
  };

  const getActiveUserId = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;

    if (data.session) {
      setSession(data.session);
    }

    const activeUserId = data.session?.user?.id || userId;
    if (!activeUserId) {
      throw new Error("Sessao expirada. Entre novamente antes de salvar.");
    }

    return activeUserId;
  };

  return {
    user,
    userId,
    accountFirstName,
    accountLastName,
    accountNeedsPasswordSetup,
    authLoading,
    authMode,
    authFirstName,
    authLastName,
    authPhone,
    authEmail,
    authPassword,
    authPasswordConfirm,
    authStatus,
    authDataStatus,
    signupConfirmationEmail,
    passwordResetEmail,
    passwordResetComplete,
    passwordRecoveryActive,
    setAuthMode: handleAuthModeChange,
    setAuthFirstName,
    setAuthLastName,
    setAuthPhone,
    setAuthEmail,
    setAuthPassword,
    setAuthPasswordConfirm,
    handleAuthSubmit,
    handleGoogleSignIn,
    handleAccountUpdate,
    handleAccountPasswordSetup,
    handleSignOut,
    getActiveUserId,
  };
}
